import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { IntegrationEmptyState } from "@/components/IntegrationEmptyState";
import { LanguageSelect, CountrySelect } from "@/components/LocalePickers";

const TYPES = ["competitor_analysis", "voc", "trend", "positioning", "market_research"];
const DEPTHS = ["shallow", "standard", "deep", "custom"];
const SOURCE_TYPES = ["web", "news", "reddit_forums", "blogs_reviews", "competitor_sites"];
const LANGUAGES = ["en", "de", "hu"];
const BRAND_FIELDS = [
  "name", "industry", "positioning", "audience",
  // Phase 7 — strategic
  "tagline", "mission", "vision", "brand_story", "brand_voice",
  "brand_values", "key_messages", "differentiators", "proof_points",
];
const PRODUCT_FIELDS = [
  "name", "category", "features", "benefits", "pricing", "icp",
  // Phase 7 — strategic
  "tagline", "product_promise", "usps", "hero_benefit",
  "jobs_to_be_done", "objections", "proof_points",
  // Phase 7 — audience deep-dive
  "audience_pains", "audience_desires", "audience_previous_attempts",
  "audience_deep_desires", "audience_expressions",
  "audience_psychographics", "audience_demographics", "audience_firmographics",
];

const STEPS = ["brand", "basics", "sources", "competitors", "focus", "details", "review"];

export default function ProjectNew() {
  const { t } = useTranslation(["projects", "common", "errors"]);
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);

  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Form state.
  const [brandId, setBrandId] = useState("");
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("voc");
  const [language, setLanguage] = useState("en");
  const [country, setCountry] = useState("");
  const [extendLanguages, setExtendLanguages] = useState(false);
  const [extendCountries, setExtendCountries] = useState(false);
  const [sourceTypes, setSourceTypes] = useState(new Set(["reddit_forums", "blogs_reviews"]));
  const [sourceTypesTouched, setSourceTypesTouched] = useState(false);
  const [competitorIds, setCompetitorIds] = useState(new Set());
  const [depth, setDepth] = useState("standard");
  // Phase 12 — source freshness window. "default" = use strategy default
  // (omit from payload), "all" = no time filter, otherwise integer days.
  const [freshnessChoice, setFreshnessChoice] = useState("default");
  const [maxSourcesOverride, setMaxSourcesOverride] = useState(100);
  const [maxPagesOverride, setMaxPagesOverride] = useState(80);
  const [customQueries, setCustomQueries] = useState("");
  const [brandFields, setBrandFields] = useState(new Set(["name"]));
  // productFields = Map<product_id, Set<field>>
  const [productFields, setProductFields] = useState(new Map());
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  // Phase 12 — wizard cost preflight. Polled when entering the review step.
  // Non-blocking: the user can still submit. The orchestrator will fail
  // mid-run with HTTP 402 if credits run out, and resume will pick up
  // already-charged stages without double-charging.
  const [costPreflight, setCostPreflight] = useState(null);
  const [costPreflightLoading, setCostPreflightLoading] = useState(false);

  // Load brand list on mount.
  useEffect(() => {
    setLoadingMeta(true);
    api.get("/brands")
      .then(({ data }) => setBrands(data))
      .catch((err) => toast.error(formatApiError(err, t("errors:generic"))))
      .finally(() => setLoadingMeta(false));
  }, [t]);

  // Reload products + competitors when brand changes, and default language/country from brand.
  useEffect(() => {
    if (!brandId) {
      setProducts([]);
      setCompetitors([]);
      setProductId("");
      setCompetitorIds(new Set());
      return;
    }
    const brand = brandsById[brandId];
    if (brand?.languages?.length) setLanguage(brand.languages[0].toLowerCase());
    if (brand?.countries?.length) setCountry(brand.countries[0].toUpperCase());
    // Default brand context fields: anything populated.
    if (brand) {
      const next = new Set(["name"]);
      if (brand.industry) next.add("industry");
      if (brand.positioning) next.add("positioning");
      if (brand.audience) next.add("audience");
      setBrandFields(next);
    }
    Promise.all([
      api.get(`/brands/${brandId}/products`),
      api.get(`/brands/${brandId}/competitors`),
    ])
      .then(([pRes, cRes]) => {
        setProducts(pRes.data);
        setCompetitors(cRes.data);
        setCompetitorIds(new Set(cRes.data.map((c) => c.id)));
        // Reset product fields map.
        setProductFields(new Map());
      })
      .catch((err) => toast.error(formatApiError(err, t("errors:generic"))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, t]);

  // Build context_fields payload from state.
  const contextFieldsPayload = useMemo(() => {
    const cf = { brand: Array.from(brandFields) };
    const prods = [];
    productFields.forEach((flags, pid) => {
      if (flags.size > 0) prods.push({ product_id: pid, fields: Array.from(flags) });
    });
    if (prods.length) cf.products = prods;
    return cf;
  }, [brandFields, productFields]);

  // Phase 12 — resolve the freshness payload value from the wizard choice.
  // "default"  → key omitted (backend falls back to strategy default).
  // "all"      → null (backend treats as no Tavily filter, no stale guard).
  // numeric    → integer days.
  const freshnessPayload = useMemo(() => {
    if (freshnessChoice === "default") return undefined; // omit key
    if (freshnessChoice === "all") return null;
    const n = parseInt(freshnessChoice, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [freshnessChoice]);

  // Live preview — debounced fetch when meaningful inputs change.
  useEffect(() => {
    if (!brandId) { setPreviewItems([]); return; }
    const id = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/research/projects/preview-queries", {
          brand_id: brandId,
          product_id: productId || null,
          type,
          language,
          country: country || null,
          depth,
          source_types: Array.from(sourceTypes),
          competitor_ids: Array.from(competitorIds),
          custom_queries: customQueries.split("\n").map((s) => s.trim()).filter(Boolean),
          extend_languages: extendLanguages,
          extend_countries: extendCountries,
          context_fields: contextFieldsPayload,
          max_sources_override: depth === "custom" ? maxSourcesOverride : null,
          max_pages_override: depth === "custom" ? maxPagesOverride : null,
          limit: 10,
        });
        setPreviewItems(data.items || []);
      } catch (e) { /* silent */ }
      finally { setPreviewLoading(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [brandId, productId, type, language, country, depth, sourceTypes,
      competitorIds, customQueries, extendLanguages, extendCountries,
      contextFieldsPayload, maxSourcesOverride, maxPagesOverride]);

  // Apply per-type default source_types when project type changes (but only until the user
  // manually edits sources — once touched, we respect their choice).
  useEffect(() => {
    if (sourceTypesTouched) return;
    if (type === "voc") {
      setSourceTypes(new Set(["reddit_forums", "blogs_reviews"]));
    } else if (type === "trend") {
      setSourceTypes(new Set(["web", "news", "blogs_reviews"]));
    } else if (type === "market_research") {
      // Phase 9 — broad-scope discovery: web + news + blogs, no reddit/competitor sites
      setSourceTypes(new Set(["web", "news", "blogs_reviews"]));
    } else {
      // competitor_analysis, positioning, etc. keep the broader default.
      setSourceTypes(new Set(SOURCE_TYPES));
    }
  }, [type, sourceTypesTouched]);

  const brandsById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);
  const showCompetitorsStep = useMemo(
    () => type === "competitor_analysis" || sourceTypes.has("competitor_sites"),
    [type, sourceTypes]
  );

  // Some steps are skipped depending on form state — compute the active step list dynamically.
  const visibleSteps = useMemo(
    () => STEPS.filter((s) => (s === "competitors" ? showCompetitorsStep : true)),
    [showCompetitorsStep]
  );
  const currentStep = visibleSteps[stepIdx];

  // Phase 12 — fetch a credit-cost preflight when the user lands on the
  // review step. Uses the same shape the orchestrator estimator returns:
  // `{estimated_credits, current_balance, sufficient}`. Driven by the count
  // of preview queries + the page cap derived from depth.
  useEffect(() => {
    if (currentStep !== "review" || !brandId) return;
    const queryCount = Math.max(1, (previewItems || []).length || 12);
    const pagesCap = depth === "shallow" ? 20 : depth === "deep" ? 80 : depth === "custom" ? (maxPagesOverride || 80) : 40;
    setCostPreflightLoading(true);
    api.post("/credits/estimate", { query_count: queryCount, pages_cap: pagesCap })
      .then(({ data }) => setCostPreflight(data))
      .catch(() => setCostPreflight(null))
      .finally(() => setCostPreflightLoading(false));
  }, [currentStep, brandId, previewItems, depth, maxPagesOverride]);

  const toggleSet = (set, value) => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  };

  const validateAndNext = () => {
    if (currentStep === "brand" && !brandId) {
      toast.error(t("projects:wizard.errors.selectBrand"));
      return;
    }
    if (currentStep === "basics" && !name.trim()) {
      toast.error(t("projects:wizard.errors.needName"));
      return;
    }
    if (stepIdx < visibleSteps.length - 1) {
      setStepIdx((s) => s + 1);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setCreateError("");
    try {
      const body = {
        name: name.trim(),
        brand_id: brandId,
        product_id: productId || null,
        type,
        language,
        country: country.trim() || null,
        depth,
        source_types: Array.from(sourceTypes),
        competitor_ids: Array.from(competitorIds),
        custom_queries: customQueries
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        extend_languages: extendLanguages,
        extend_countries: extendCountries,
        context_fields: contextFieldsPayload,
        max_sources_override: depth === "custom" ? maxSourcesOverride : null,
        max_pages_override: depth === "custom" ? maxPagesOverride : null,
        ...(freshnessPayload !== undefined ? { freshness_days: freshnessPayload } : {}),
      };
      const { data } = await api.post("/research/projects", body);
      toast.success(t("projects:detail.saveSuccess"));
      navigate(`/projects/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && /not_configured/.test(detail)) {
        setCreateError(detail);
      } else {
        toast.error(formatApiError(err, t("errors:generic")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMeta) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="space-y-6" data-testid="project-new-no-brands">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("projects:detail.backToList")}
        </Link>
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
            {t("projects:wizard.steps.brand.title")}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            {t("projects:wizard.steps.brand.subtitle")}
          </p>
          <Button asChild className="mt-6 bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/brands">{t("projects:wizard.errors.selectBrand")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="project-new-page">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/projects"
          data-testid="project-new-back"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("projects:detail.backToList")}
        </Link>
      </div>

      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <Sparkles className="mr-1 inline h-3 w-3" strokeWidth={2} /> EvidenceLab
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {t("projects:wizard.title")}
        </h1>
        <p className="max-w-xl text-sm text-slate-500">{t("projects:wizard.subtitle")}</p>
      </header>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 text-xs">
        {visibleSteps.map((s, i) => (
          <div
            key={s}
            data-testid={`project-step-${s}${i === stepIdx ? "-active" : ""}`}
            className={`flex items-center gap-1 rounded-full px-3 py-1 ${
              i === stepIdx
                ? "bg-slate-900 text-white"
                : i < stepIdx
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-500"
            }`}
          >
            {i < stepIdx ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <span>{i + 1}</span>}
            {t(`projects:wizard.steps.${s}.title`)}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="project-new-card">
        {currentStep === "brand" && (
          <StepBrand
            brands={brands}
            products={products}
            brandId={brandId}
            setBrandId={setBrandId}
            productId={productId}
            setProductId={setProductId}
          />
        )}
        {currentStep === "basics" && (
          <StepBasics
            name={name}
            setName={setName}
            type={type}
            setType={setType}
            language={language}
            setLanguage={setLanguage}
            country={country}
            setCountry={setCountry}
          />
        )}
        {currentStep === "sources" && (
          <StepSources
            sourceTypes={sourceTypes}
            setSourceTypes={(next) => { setSourceTypesTouched(true); setSourceTypes(next); }}
            extendLanguages={extendLanguages}
            setExtendLanguages={setExtendLanguages}
            extendCountries={extendCountries}
            setExtendCountries={setExtendCountries}
            type={type}
          />
        )}
        {currentStep === "competitors" && (
          <StepCompetitors
            competitors={competitors}
            competitorIds={competitorIds}
            setCompetitorIds={setCompetitorIds}
            brand={brandsById[brandId]}
          />
        )}
        {currentStep === "focus" && (
          <StepFocus
            brand={brandsById[brandId]}
            products={products}
            brandFields={brandFields}
            setBrandFields={setBrandFields}
            productFields={productFields}
            setProductFields={setProductFields}
            previewItems={previewItems}
            previewLoading={previewLoading}
          />
        )}
        {currentStep === "details" && (
          <StepDetails
            depth={depth}
            setDepth={setDepth}
            freshnessChoice={freshnessChoice}
            setFreshnessChoice={setFreshnessChoice}
            customQueries={customQueries}
            setCustomQueries={setCustomQueries}
            maxSourcesOverride={maxSourcesOverride}
            setMaxSourcesOverride={setMaxSourcesOverride}
            maxPagesOverride={maxPagesOverride}
            setMaxPagesOverride={setMaxPagesOverride}
            brandId={brandId}
            type={type}
            language={language}
            productIds={products.map((p) => p.id).filter((pid) => productFields.get(pid)?.size > 0)}
            contextFields={contextFieldsPayload}
            aiSuggestions={aiSuggestions}
            setAiSuggestions={setAiSuggestions}
            aiSuggesting={aiSuggesting}
            setAiSuggesting={setAiSuggesting}
          />
        )}
        {currentStep === "review" && (
          <StepReview
            name={name}
            type={type}
            brand={brandsById[brandId]}
            language={language}
            depth={depth}
            sourceTypes={Array.from(sourceTypes)}
            competitorCount={competitorIds.size}
            customQueries={customQueries}
            createError={createError}
            previewItems={previewItems}
            previewLoading={previewLoading}
          />
        )}

        <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
            disabled={stepIdx === 0}
            data-testid="project-new-back-step"
          >
            <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("projects:wizard.back")}
          </Button>
          {currentStep === "review" ? (
            <div className="flex flex-col items-end gap-2">
              {costPreflight && !costPreflight.sufficient ? (
                <div
                  className="flex w-full max-w-md items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  data-testid="wizard-cost-warning"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <p className="font-medium">
                      {t("credits:wizard_warning.title", {
                        ns: "credits",
                        estimate: costPreflight.estimated_credits,
                      })}
                    </p>
                    <p className="mt-0.5 leading-snug">
                      {t("credits:wizard_warning.body", {
                        ns: "credits",
                        available: costPreflight.current_balance,
                      })}{" "}
                      <Link
                        to="/settings/credits"
                        data-testid="wizard-cost-manage-link"
                        className="font-medium underline underline-offset-2"
                      >
                        {t("credits:wizard_warning.cta_manage", { ns: "credits" })}
                      </Link>
                      {" →"}
                    </p>
                  </div>
                </div>
              ) : null}
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="project-new-submit"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {t("projects:wizard.submit")} <ChevronRight className="ml-1 h-4 w-4" strokeWidth={2} />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={validateAndNext}
              data-testid="project-new-next-step"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {t("projects:wizard.next")} <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBrand({ brands, products, brandId, setBrandId, productId, setProductId }) {
  const { t } = useTranslation("projects");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.brand.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.brand.subtitle")}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("wizard.steps.brand.brandLabel")}
          </Label>
          <Select value={brandId} onValueChange={setBrandId}>
            <SelectTrigger data-testid="project-brand-select" className="h-11">
              <SelectValue placeholder={t("wizard.steps.brand.brandPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {brands.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("wizard.steps.brand.productLabel")}
          </Label>
          <Select value={productId || "__none__"} onValueChange={(v) => setProductId(v === "__none__" ? "" : v)} disabled={!brandId || products.length === 0}>
            <SelectTrigger data-testid="project-product-select" className="h-11">
              <SelectValue placeholder={t("wizard.steps.brand.productPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("wizard.steps.brand.productNone")}</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function StepBasics({ name, setName, type, setType, language, setLanguage, country, setCountry }) {
  const { t } = useTranslation("projects");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.basics.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.basics.subtitle")}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("wizard.steps.basics.nameLabel")}
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("wizard.steps.basics.namePlaceholder")}
          data-testid="project-name-input"
          className="h-11"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("wizard.steps.basics.typeLabel")}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map((tp) => (
              <button
                type="button"
                key={tp}
                onClick={() => setType(tp)}
                data-testid={`project-type-${tp}`}
                className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                  type === tp
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t(`types.${tp}`)}
              </button>
            ))}
          </div>
          <p
            data-testid="project-type-description"
            className="text-[11px] leading-snug text-slate-500"
          >
            {t(`typeDescriptions.${type}`, { defaultValue: "" })}
          </p>
          {/* Phase 9.5 — show the research framework that's baked into the
              orchestrator for this type. Makes the strategy choice tangible. */}
          <div
            className="mt-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2"
            data-testid={`project-type-framework-${type}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
              {t(`frameworks.${type}.label`)}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-indigo-900/80">
              {t(`frameworks.${type}.summary`)}
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("wizard.steps.basics.languageLabel")}
            </Label>
            <LanguageSelect value={language} onChange={setLanguage} dataTestid="project-language-select" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("wizard.steps.basics.countryLabel")}
            </Label>
            <CountrySelect value={country} onChange={setCountry} dataTestid="project-country-select" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepSources({ sourceTypes, setSourceTypes, extendLanguages, setExtendLanguages, extendCountries, setExtendCountries, type }) {
  const { t } = useTranslation("projects");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.sources.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.sources.subtitle")}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {SOURCE_TYPES.map((st) => (
          <label
            key={st}
            data-testid={`project-source-${st}`}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
              sourceTypes.has(st)
                ? "border-indigo-300 bg-indigo-50/50"
                : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              checked={sourceTypes.has(st)}
              onChange={() => {
                const next = new Set(sourceTypes);
                next.has(st) ? next.delete(st) : next.add(st);
                setSourceTypes(next);
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <p className="font-display text-sm font-semibold text-slate-900">{t(`sourceTypes.${st}`)}</p>
              <p className="mt-0.5 text-xs text-slate-500">{t(`sourceTypes.${st}Hint`)}</p>
            </div>
          </label>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="font-display text-sm font-semibold text-slate-900">{t("wizard.steps.sources.scopeTitle")}</h3>
        <p className="mt-0.5 text-xs text-slate-500">{t("wizard.steps.sources.scopeSubtitle")}</p>
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="project-extend-languages-wrap">
            <input
              type="checkbox"
              checked={extendLanguages}
              onChange={(e) => setExtendLanguages(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              data-testid="project-extend-languages"
            />
            <span>{t("wizard.steps.sources.extendLanguages")}</span>
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="project-extend-countries-wrap">
            <input
              type="checkbox"
              checked={extendCountries}
              onChange={(e) => setExtendCountries(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              data-testid="project-extend-countries"
            />
            <span>{t("wizard.steps.sources.extendCountries")}</span>
          </label>
        </div>
        {type === "trend" && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="project-trend-hint">
            {t("wizard.steps.sources.trendHint")}
          </p>
        )}
      </div>
    </div>
  );
}

function StepCompetitors({ competitors, competitorIds, setCompetitorIds, brand }) {
  const { t } = useTranslation("projects");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.competitors.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.competitors.subtitle")}</p>
      </div>
      {competitors.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
          {t("wizard.steps.competitors.empty")}{" "}
          {brand && (
            <Link to={`/brands/${brand.id}`} className="text-indigo-700 hover:underline">
              {brand.name}
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500" data-testid="competitor-selected-count">
              {t("wizard.steps.competitors.selected", { count: competitorIds.size })}
            </p>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={() => setCompetitorIds(new Set(competitors.map((c) => c.id)))}
                className="font-medium text-indigo-700 hover:underline"
                data-testid="competitor-select-all"
              >
                {t("wizard.steps.competitors.selectAll")}
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={() => setCompetitorIds(new Set())}
                className="font-medium text-slate-500 hover:underline"
                data-testid="competitor-deselect-all"
              >
                {t("wizard.steps.competitors.deselectAll")}
              </button>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {competitors.map((c) => (
              <label
                key={c.id}
                data-testid={`project-competitor-${c.id}`}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                  competitorIds.has(c.id)
                    ? "border-indigo-300 bg-indigo-50/50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={competitorIds.has(c.id)}
                  onChange={() => {
                    const next = new Set(competitorIds);
                    next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                    setCompetitorIds(next);
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                  {c.url && (
                    <p className="mt-0.5 truncate text-[11px] text-slate-500">
                      {c.url.replace(/^https?:\/\//, "")}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StepFocus({ brand, products, brandFields, setBrandFields,
                    productFields, setProductFields, previewItems, previewLoading }) {
  const { t } = useTranslation("projects");

  const toggleBrandField = (field) => {
    setBrandFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const toggleProductField = (productId, field) => {
    setProductFields((prev) => {
      const next = new Map(prev);
      const flags = new Set(next.get(productId) || []);
      if (flags.has(field)) flags.delete(field); else flags.add(field);
      next.set(productId, flags);
      return next;
    });
  };

  const brandFieldFilled = (field) => {
    if (!brand) return false;
    const arrayFields = ["brand_values", "key_messages", "differentiators", "proof_points"];
    if (arrayFields.includes(field)) {
      return Array.isArray(brand[field]) && brand[field].length > 0;
    }
    if (field === "name") return !!brand.name;
    if (field === "industry") return !!brand.industry;
    if (field === "positioning") return !!brand.positioning;
    if (field === "audience") return !!brand.audience;
    if (field === "tagline") return !!brand.tagline;
    if (field === "mission") return !!brand.mission;
    if (field === "vision") return !!brand.vision;
    if (field === "brand_story") return typeof brand.brand_story === "string" && brand.brand_story.trim().length > 0;
    if (field === "brand_voice") {
      const v = brand.brand_voice;
      if (!v || typeof v !== "object") return false;
      return !!(v.tone || v.style ||
        (Array.isArray(v.do) && v.do.length) ||
        (Array.isArray(v.dont) && v.dont.length) ||
        (Array.isArray(v.examples) && v.examples.length));
    }
    return false;
  };

  const productFieldFilled = (product, field) => {
    const arrayFields = [
      "features", "benefits", "usps", "jobs_to_be_done", "objections", "proof_points",
      "audience_pains", "audience_desires", "audience_previous_attempts",
      "audience_deep_desires", "audience_expressions", "audience_psychographics",
    ];
    if (arrayFields.includes(field)) {
      return Array.isArray(product[field]) && product[field].length > 0;
    }
    if (field === "name") return !!product.name;
    if (field === "category") return !!product.category;
    if (field === "pricing") return !!product.pricing || !!product.pricing_notes;
    if (field === "icp") return !!product.icp;
    if (field === "tagline") return !!product.tagline;
    if (field === "product_promise") return !!product.product_promise;
    if (field === "hero_benefit") return !!product.hero_benefit;
    if (field === "audience_demographics") {
      const d = product.audience_demographics;
      return !!(d && typeof d === "object" && Object.values(d).some((v) => !!v));
    }
    if (field === "audience_firmographics") {
      const f = product.audience_firmographics;
      if (!f || typeof f !== "object") return false;
      return !!(f.company_size || f.industry || f.geo || (f.role_titles?.length));
    }
    return false;
  };

  // Compute "all empty" — show a soft warning banner without blocking save.
  const hasAnyProductFields = (() => {
    if (!productFields || productFields.size === 0) return false;
    for (const flags of productFields.values()) if (flags && flags.size > 0) return true;
    return false;
  })();
  const showEmptyContextWarning = brandFields.size === 0 && !hasAnyProductFields;

  return (
    <div className="space-y-6" data-testid="project-focus-step">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.focus.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.focus.subtitle")}</p>
      </div>

      {/* Brand context */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="project-focus-brand-card">
        <h3 className="font-display text-sm font-semibold text-slate-900">
          {t("wizard.steps.focus.brandTitle")}
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">{t("wizard.steps.focus.brandHint")}</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {BRAND_FIELDS.map((field) => {
            const filled = brandFieldFilled(field);
            const checked = brandFields.has(field);
            return (
              <label
                key={field}
                data-testid={`project-focus-brand-${field}`}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  checked ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white"
                } ${!filled ? "opacity-60" : "cursor-pointer hover:bg-slate-50"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!filled}
                  onChange={() => toggleBrandField(field)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{t(`wizard.steps.focus.field.${field}`)}</p>
                  {!filled && (
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {t("wizard.steps.focus.fieldEmpty")}
                    </p>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Product context */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="project-focus-products-card">
        <h3 className="font-display text-sm font-semibold text-slate-900">
          {t("wizard.steps.focus.productsTitle")}
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">{t("wizard.steps.focus.productsHint")}</p>
        {products.length === 0 ? (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
            {t("wizard.steps.focus.fieldEmpty")}
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {products.map((p) => {
              const flags = productFields.get(p.id) || new Set();
              return (
                <div
                  key={p.id}
                  className="rounded-lg border border-slate-100 bg-slate-50/40 p-3"
                  data-testid={`project-focus-product-${p.id}`}
                >
                  <p className="font-display text-sm font-semibold text-slate-900">{p.name}</p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {PRODUCT_FIELDS.map((field) => {
                      const filled = productFieldFilled(p, field);
                      const checked = flags.has(field);
                      return (
                        <label
                          key={field}
                          data-testid={`project-focus-product-${p.id}-${field}`}
                          className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-xs transition-colors ${
                            checked ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white"
                          } ${!filled ? "opacity-60" : "cursor-pointer hover:bg-slate-50"}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!filled}
                            onChange={() => toggleProductField(p.id, field)}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{t(`wizard.steps.focus.field.${field}`)}</p>
                            {!filled && (
                              <p className="mt-0.5 text-[10px] text-slate-400">
                                {t("wizard.steps.focus.fieldEmpty")}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Live preview */}
      {showEmptyContextWarning && (
        <div
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3"
          data-testid="project-focus-empty-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" strokeWidth={1.75} />
          <p className="text-sm text-amber-900">{t("wizard.steps.focus.emptyWarning")}</p>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="project-focus-preview">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold text-slate-900">
            {t("wizard.steps.focus.previewTitle")}
          </h3>
          {previewLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" data-testid="project-focus-preview-loading" />
          )}
        </div>
        <p className="mt-0.5 text-xs text-slate-500">
          {t("wizard.steps.focus.previewSubtitle", { n: Math.min(5, previewItems.length || 5) })}
        </p>
        {previewItems.length === 0 ? (
          <p
            className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400"
            data-testid="project-focus-preview-empty"
          >
            {previewLoading ? t("wizard.steps.focus.previewLoading") : t("wizard.steps.focus.previewEmpty")}
          </p>
        ) : (
          <ol className="mt-3 space-y-2" data-testid="project-focus-preview-list">
            {previewItems.slice(0, 5).map((p, i) => (
              <li
                key={`${p.query}-${i}`}
                data-testid={`project-focus-preview-item-${i}`}
                className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/40 px-3 py-2"
              >
                <span className="break-all font-mono text-[12px] text-slate-700">{p.query}</span>
                <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
                  {p.source_type}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function StepDetails({ depth, setDepth,
                      freshnessChoice, setFreshnessChoice,
                      customQueries, setCustomQueries,
                      maxSourcesOverride, setMaxSourcesOverride,
                      maxPagesOverride, setMaxPagesOverride,
                      brandId, type, language, productIds, contextFields,
                      aiSuggestions, setAiSuggestions, aiSuggesting, setAiSuggesting }) {
  const { t } = useTranslation(["projects", "errors"]);
  // Phase 12 — strategy defaults (mirror backend ``default_freshness_days``).
  // Surfaced as a hint next to the "Strategy default" option.
  const STRATEGY_FRESHNESS_DEFAULTS = {
    voc: 365, positioning: 730, competitor_analysis: 365,
    trend: 180, market_research: 730,
  };
  const strategyDefault = STRATEGY_FRESHNESS_DEFAULTS[type] || 365;
  const runAi = async () => {
    if (!brandId) return;
    setAiSuggesting(true);
    try {
      const { data } = await api.post("/research/projects/suggest-queries", {
        brand_id: brandId,
        product_ids: productIds || [],
        project_type: type,
        language,
        context_fields: contextFields,
      });
      setAiSuggestions(data.items || []);
      if (!data.items || data.items.length === 0) {
        toast.message(t("projects:wizard.steps.details.aiSuggestEmpty"));
      }
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setAiSuggesting(false); }
  };
  const appendChip = (q) => {
    setCustomQueries((cq) => (cq ? cq.replace(/\s+$/, "") + "\n" + q : q));
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("projects:wizard.steps.details.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("projects:wizard.steps.details.subtitle")}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("projects:wizard.steps.details.depthLabel")}
        </Label>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {DEPTHS.map((d) => (
            <button
              type="button"
              key={d}
              onClick={() => setDepth(d)}
              data-testid={`project-depth-${d}`}
              className={`rounded-xl border p-4 text-left transition-colors ${
                depth === d
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <p className="font-display text-sm font-semibold">{t(`projects:depth.${d}`)}</p>
              <p className={`mt-1 text-[11px] ${depth === d ? "text-slate-300" : "text-slate-500"}`}>
                {t(`projects:depth.${d}Hint`)}
              </p>
            </button>
          ))}
        </div>
        {depth === "custom" && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="project-custom-depth-panel">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">{t("projects:wizard.steps.details.customMaxSources")}</Label>
              <Input
                type="number" min={10} max={500}
                value={maxSourcesOverride}
                onChange={(e) => setMaxSourcesOverride(Math.max(10, Math.min(500, parseInt(e.target.value || "0", 10) || 100)))}
                data-testid="project-max-sources"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">{t("projects:wizard.steps.details.customMaxPages")}</Label>
              <Input
                type="number" min={5} max={300}
                value={maxPagesOverride}
                onChange={(e) => setMaxPagesOverride(Math.max(5, Math.min(300, parseInt(e.target.value || "0", 10) || 80)))}
                data-testid="project-max-pages"
              />
            </div>
            <p className="sm:col-span-2 text-xs text-slate-400">{t("projects:wizard.steps.details.customHelp")}</p>
          </div>
        )}
      </div>
      {/* Phase 12 — source freshness window */}
      <div className="space-y-2" data-testid="project-freshness-panel">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("projects:wizard.steps.details.freshnessLabel")}
        </Label>
        <select
          value={freshnessChoice}
          onChange={(e) => setFreshnessChoice(e.target.value)}
          className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-slate-900 focus:outline-none sm:max-w-sm"
          data-testid="project-freshness-select"
        >
          <option value="default">
            {t("projects:wizard.steps.details.freshnessDefault", { days: strategyDefault })}
          </option>
          <option value="30">{t("projects:wizard.steps.details.freshness30d")}</option>
          <option value="90">{t("projects:wizard.steps.details.freshness3mo")}</option>
          <option value="180">{t("projects:wizard.steps.details.freshness6mo")}</option>
          <option value="365">{t("projects:wizard.steps.details.freshness1y")}</option>
          <option value="730">{t("projects:wizard.steps.details.freshness2y")}</option>
          <option value="all">{t("projects:wizard.steps.details.freshnessAll")}</option>
        </select>
        <p className="text-xs text-slate-400">{t("projects:wizard.steps.details.freshnessHelp")}</p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("projects:wizard.steps.details.customQueriesLabel")}
          </Label>
          <Button
            type="button" variant="outline" size="sm"
            disabled={!brandId || aiSuggesting}
            onClick={runAi}
            data-testid="project-ai-suggest-button"
          >
            {aiSuggesting ? (
              <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> {t("projects:wizard.steps.details.aiSuggesting")}</>
            ) : (
              <><Sparkles className="mr-2 h-3.5 w-3.5" /> {t("projects:wizard.steps.details.aiSuggest")}</>
            )}
          </Button>
        </div>
        {aiSuggestions.length > 0 && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="project-ai-suggestions">
            <p className="mb-2 text-[11px] uppercase tracking-wider text-indigo-700">{t("projects:wizard.steps.details.aiSuggestHint")}</p>
            <div className="flex flex-wrap gap-2">
              {aiSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => appendChip(s)}
                  className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs text-slate-800 hover:border-indigo-400 hover:bg-indigo-50"
                  data-testid={`project-ai-chip-${i}`}
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <Textarea
          rows={4}
          value={customQueries}
          onChange={(e) => setCustomQueries(e.target.value)}
          placeholder={t("projects:wizard.steps.details.customQueriesPlaceholder")}
          data-testid="project-custom-queries"
        />
        <p className="text-xs text-slate-400">{t("projects:wizard.steps.details.customQueriesHint")}</p>
      </div>
    </div>
  );
}

function StepReview({ name, type, brand, language, depth, sourceTypes, competitorCount, customQueries, createError }) {
  const { t } = useTranslation("projects");
  const lines = customQueries.split("\n").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("wizard.steps.review.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.steps.review.subtitle")}</p>
      </div>
      <dl className="grid gap-3 sm:grid-cols-2">
        <ReviewItem label={t("wizard.steps.review.labelType")} value={t(`types.${type}`)} />
        <ReviewItem label={t("wizard.steps.review.labelBrand")} value={brand?.name || "—"} />
        <ReviewItem label={t("wizard.steps.review.labelLanguage")} value={language.toUpperCase()} />
        <ReviewItem label={t("wizard.steps.review.labelDepth")} value={t(`depth.${depth}`)} />
        <ReviewItem
          label={t("wizard.steps.review.labelSources")}
          value={sourceTypes.map((s) => t(`sourceTypes.${s}`)).join(" · ")}
        />
        <ReviewItem
          label={t("wizard.steps.review.labelCompetitors")}
          value={String(competitorCount)}
        />
        <ReviewItem
          label={t("wizard.steps.review.labelCustomQueries")}
          value={lines.length ? `${lines.length}` : "—"}
        />
      </dl>
      <p className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900">
        {t("wizard.steps.review.ready")} {name && <span className="font-semibold">— &quot;{name}&quot;</span>}
      </p>
      {createError === "firecrawl_not_configured" && (
        <IntegrationEmptyState integration="firecrawl" variant="compact" />
      )}
      {createError === "tavily_not_configured" && (
        <IntegrationEmptyState integration="tavily" variant="compact" />
      )}
    </div>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-900">{value || "—"}</p>
    </div>
  );
}
